<template>
  <div class="market-report">
    <header id="blog-header">
      <div class="inner">
        <h1 class="page-title">每日市场报告</h1>
        <span class="page-desc">期权监控 · 大宗商品 · 货币汇率</span>
      </div>
    </header>

    <div class="report-content">
      <!-- 日期选择 -->
      <div class="report-toolbar">
        <input v-model="queryDate" placeholder="YYYY-MM-DD（留空看最新）" @keyup.enter="loadReport">
        <button @click="loadReport">查询</button>
        <span v-if="report" class="report-status">
          {{ report.report_date }} ·
          <span :class="'status-' + report.status">{{ statusLabel(report.status) }}</span>
          <template v-if="report.channel"> · 通道 {{ report.channel }}</template>
        </span>
      </div>

      <div v-if="loading" class="loading">加载中...</div>
      <div v-if="error" class="error-msg">{{ error }}</div>

      <!-- A股证券化率(巴菲特指标) -->
      <div class="buffett-panel" v-if="buffett">
        <div class="buffett-head">
          <h2>A股证券化率(总市值/上年GDP)</h2>
          <button @click="refreshBuffett" :disabled="buffettLoading">
            {{ buffettLoading ? '刷新中...' : '刷新' }}
          </button>
          <span class="buffett-time">采集于 {{ buffett.captured_at }}<template v-if="buffettJustRefreshed"> · 已刷新</template></span>
        </div>
        <div class="buffett-body">
          <div class="buffett-ratio" :class="'verdict-' + buffett.verdict">
            {{ buffett.ratio_pct.toFixed(1) }}%
            <span class="buffett-band">{{ buffett.detail && buffett.detail.band }}</span>
          </div>
          <div class="buffett-meta">
            A股总市值 {{ (buffett.total_mktcap_100m / 10000).toFixed(2) }} 万亿元 ÷
            上年GDP {{ (buffett.gdp_100m / 10000).toFixed(2) }} 万亿元
            (统计 {{ buffett.stock_count }} 只,含沪/深/北交所)
          </div>
          <div class="buffett-note" v-if="buffettError">{{ buffettError }}</div>
          <a class="buffett-toggle" @click="showBuffettDetail = !showBuffettDetail">
            {{ showBuffettDetail ? '收起评估过程' : '查看评估过程' }}
          </a>
          <ol class="eval-steps" v-if="showBuffettDetail && buffett.detail && buffett.detail.steps">
            <li v-for="(s, si) in buffett.detail.steps" :key="si">{{ s }}</li>
          </ol>
        </div>
      </div>

      <!-- 报告正文（纯文本渲染） -->
      <pre v-if="report" class="report-body">{{ report.content_md }}</pre>

      <!-- 估值总览 -->
      <div class="valuation-panel" v-if="valuations.length > 0">
        <h2>估值总览
          <span class="model-name" v-if="modelName">评估模型: {{ modelName }}</span>
        </h2>
        <p class="model-hint">点击行查看该标的的完整模型评估过程(因子得分 → 加权综合分 → 规则判定)</p>
        <table class="valuation-table">
          <thead>
            <tr>
              <th>标的</th><th>名称</th><th>类别</th>
              <th>1年Z</th><th>5年Z</th><th>52周分位</th><th>比价Z</th><th>综合分</th><th>结论</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="v in valuations">
              <tr :key="v.symbol" class="valuation-row" @click="toggleExpand(v.symbol)">
                <td class="ticker-cell">{{ v.symbol }}</td>
                <td>{{ v.name }}</td>
                <td>{{ categoryLabel(v.category) }}</td>
                <td>{{ fmt(v.z_score_1y) }}</td>
                <td>{{ fmt(v.z_score_5y) }}</td>
                <td>{{ v.percentile_52w != null ? v.percentile_52w.toFixed(0) + '%' : '-' }}</td>
                <td>{{ fmt(v.ratio_z_score) }}</td>
                <td :class="compositeClass(v)">{{ compositeOf(v) }}</td>
                <td>
                  <span :class="'verdict-' + v.verdict">{{ verdictLabel(v.verdict) }}</span>
                  <span v-if="v.detail && v.detail.confidence && v.detail.confidence !== '无'" class="confidence">
                    置信{{ v.detail.confidence }}
                  </span>
                </td>
              </tr>
              <tr v-if="expandedSymbol === v.symbol && v.detail" :key="v.symbol + '-detail'" class="detail-row">
                <td colspan="9">
                  <div class="eval-process">
                    <h4>模型评估过程 — {{ v.name }} ({{ v.symbol }})</h4>
                    <table class="factor-table" v-if="v.detail.factors">
                      <thead>
                        <tr><th>因子</th><th>原始值</th><th>得分(z量纲)</th><th>权重</th><th>贡献</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="f in v.detail.factors" :key="f.key">
                          <td>{{ f.label }}</td>
                          <td>{{ f.value != null ? f.value : '缺失' }}</td>
                          <td>{{ f.score != null ? f.score.toFixed(2) : '-' }}</td>
                          <td>{{ f.weight.toFixed(2) }}</td>
                          <td>{{ f.score != null ? (f.score * f.weight).toFixed(3) : '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <ol class="eval-steps" v-if="v.detail.steps">
                      <li v-for="(s, si) in v.detail.steps" :key="si">{{ s }}</li>
                    </ol>
                    <p class="eval-warning" v-if="v.detail.warning">⚠ {{ v.detail.warning }}</p>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'market-report',
  data: function () {
    return {
      report: null,
      valuations: [],
      queryDate: '',
      expandedSymbol: '',
      loading: false,
      error: '',
      buffett: null,
      buffettLoading: false,
      buffettError: '',
      buffettJustRefreshed: false,
      showBuffettDetail: false
    }
  },
  computed: {
    modelName: function () {
      var first = this.valuations.find(function (v) { return v.detail && v.detail.model })
      return first ? first.detail.model : ''
    }
  },
  mounted: function () {
    this.loadReport()
    this.loadValuations()
    this.loadBuffett(false)
  },
  methods: {
    loadBuffett: function (refresh) {
      var self = this
      self.buffettLoading = true
      self.buffettError = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/buffett/' + (refresh ? '?refresh=1' : '')
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.buffett = res.latest
          self.buffettJustRefreshed = !!res.refreshed
          if (refresh && !res.refreshed) {
            self.buffettError = '10分钟内已有快照,展示最近一次采集结果'
          }
        }
        self.buffettLoading = false
      }).catch(function (err) {
        self.buffettLoading = false
        try {
          var res = JSON.parse(err.bodyText)
          self.buffettError = res.message || '证券化率加载失败'
          if (res.latest) self.buffett = res.latest
        } catch (e) {
          self.buffettError = '证券化率加载失败'
        }
      })
    },

    refreshBuffett: function () {
      this.loadBuffett(true)
    },
    loadReport: function () {
      var self = this
      self.loading = true
      self.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/report/' +
        (this.queryDate ? this.queryDate + '/' : 'latest/')
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.report = res.report
        } else {
          self.report = null
          self.error = res.message || '加载失败'
        }
        self.loading = false
      }).catch(function (err) {
        self.report = null
        self.error = err.status === 404 ? '报告不存在' : ('请求失败: ' + err.statusText)
        self.loading = false
      })
    },

    loadValuations: function () {
      var self = this
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/valuations/'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.valuations = res.valuations
        }
      }).catch(function (err) {
        console.error('加载估值失败:', err)
      })
    },

    fmt: function (val) {
      return val != null ? Number(val).toFixed(2) : '-'
    },

    toggleExpand: function (symbol) {
      this.expandedSymbol = this.expandedSymbol === symbol ? '' : symbol
    },

    compositeOf: function (v) {
      if (!v.detail || v.detail.composite == null) return '-'
      var c = v.detail.composite
      return (c > 0 ? '+' : '') + c.toFixed(2)
    },

    compositeClass: function (v) {
      if (!v.detail || v.detail.composite == null) return ''
      return Math.abs(v.detail.composite) >= (v.detail.threshold || 1.0) ? 'composite-active' : ''
    },

    statusLabel: function (status) {
      return { generated: '已生成', sent: '已发送', send_failed: '发送失败' }[status] || status
    },

    categoryLabel: function (category) {
      return {
        commodity_future: '商品期货',
        commodity_stock: '商品股票',
        fx: '汇率',
        index: '指数'
      }[category] || category
    },

    verdictLabel: function (verdict) {
      return { undervalued: '低估', overvalued: '高估', neutral: '中性' }[verdict] || verdict
    }
  }
}
</script>

<style scoped>
.market-report {
  min-height: 100vh;
}

#blog-header {
  padding: 3rem 1rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.page-desc {
  color: #888;
}

.report-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
}

.report-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.report-toolbar input {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  min-width: 220px;
}

.report-toolbar button {
  padding: 6px 14px;
}

.report-status {
  font-size: 13px;
  color: #888;
}

.status-sent { color: #2e7d32; }
.status-send_failed { color: #c62828; }
.status-generated { color: #f9a825; }

.loading, .error-msg {
  padding: 1rem 0;
}

.error-msg { color: #c62828; }

.report-body {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 14px;
  line-height: 1.7;
}

.valuation-panel {
  margin-top: 24px;
}

.valuation-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.valuation-table th, .valuation-table td {
  border-bottom: 1px solid #eee;
  padding: 6px 8px;
  text-align: left;
}

.ticker-cell {
  font-family: monospace;
  font-weight: bold;
}

.verdict-undervalued { color: #2e7d32; font-weight: bold; }
.verdict-overvalued { color: #c62828; font-weight: bold; }
.verdict-neutral { color: #999; }

/* 证券化率模块 */
.buffett-panel {
  margin: 0 0 20px;
  padding: 14px 18px;
  border: 1px solid #d8e4f0;
  border-radius: 6px;
  background: #f7fafd;
}

.buffett-head {
  display: flex;
  gap: 12px;
  align-items: center;
}

.buffett-head h2 {
  font-size: 16px;
  margin: 0;
}

.buffett-head button {
  padding: 4px 12px;
}

.buffett-time {
  font-size: 12px;
  color: #999;
  margin-left: auto;
}

.buffett-ratio {
  font-size: 32px;
  font-weight: bold;
  margin: 8px 0 4px;
}

.buffett-band {
  font-size: 14px;
  margin-left: 8px;
}

.buffett-meta {
  font-size: 13px;
  color: #666;
}

.buffett-note {
  font-size: 12px;
  color: #b26a00;
  margin-top: 4px;
}

.buffett-toggle {
  display: inline-block;
  font-size: 12px;
  color: #3273dc;
  cursor: pointer;
  margin-top: 6px;
}

.model-name {
  font-size: 13px;
  font-weight: normal;
  color: #888;
  margin-left: 10px;
  font-family: monospace;
}

.model-hint {
  font-size: 12px;
  color: #aaa;
  margin: 4px 0 8px;
}

.valuation-row {
  cursor: pointer;
}

.valuation-row:hover {
  background: #f5f8ff;
}

.composite-active {
  font-weight: bold;
}

.confidence {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.detail-row td {
  background: #fafcff;
  padding: 12px 16px;
}

.eval-process h4 {
  margin: 0 0 8px;
  font-size: 14px;
}

.factor-table {
  width: auto;
  min-width: 60%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 10px;
}

.factor-table th, .factor-table td {
  border: 1px solid #e0e8f0;
  padding: 4px 10px;
  text-align: right;
}

.factor-table th:first-child, .factor-table td:first-child {
  text-align: left;
}

.eval-steps {
  font-size: 12px;
  color: #555;
  line-height: 1.8;
  padding-left: 18px;
  margin: 0;
}

.eval-warning {
  color: #c62828;
  font-size: 12px;
  margin: 8px 0 0;
}
</style>
